import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import { fetchPublicHero } from '@/services/siteHero'

const OVERLAY_CLASSES = {
  none: '',
  dark: 'bg-black/[0.42]',
  light: 'bg-white/[0.32]',
  inverted: 'bg-white/[0.18] mix-blend-overlay ring-1 ring-black/15',
}

const TEXT_EFFECT_CLASSES = {
  none: '',
  'shadow-soft': 'drop-shadow-[0_2px_14px_rgba(0,0,0,0.12)] sm:drop-shadow-[0_2px_10px_rgba(0,0,0,0.08)]',
  'shadow-strong': 'drop-shadow-[0_6px_32px_rgba(0,0,0,0.28)]',
  'glow-soft': '[text-shadow:0_0_26px_rgba(255,255,255,0.45)]',
  'invert-text': 'relative z-[2] mx-auto mix-blend-difference text-neutral-950',
}

/** @param {string | undefined | null} key */
function textMotionBlocks(key) {
  switch (key) {
    case 'fade':
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.48 },
      }
    case 'slide-x':
      return {
        initial: { opacity: 0, x: 28 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -22 },
        transition: { duration: 0.5 },
      }
    case 'scale':
      return {
        initial: { opacity: 0, scale: 0.94 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 1.03 },
        transition: { duration: 0.48 },
      }
    case 'none':
      return {
        initial: { opacity: 1 },
        animate: { opacity: 1 },
        exit: { opacity: 1 },
        transition: { duration: 0 },
      }
    case 'fade-up':
    default:
      return {
        initial: { opacity: 0, y: 26 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -14 },
        transition: { duration: 0.52 },
      }
  }
}

/** @param {string | undefined | null} mode */
function useBackgroundMotion(mode) {
  return useMemo(() => {
    switch (mode) {
      case 'ken-burns':
        return {
          animate: {
            scale: [1, 1.09],
            x: ['0%', '-1.25%'],
            y: ['0%', '-1%'],
          },
          transition: { duration: 28, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' },
        }
      case 'slow-zoom':
        return {
          animate: { scale: [1, 1.056] },
          transition: { duration: 36, repeat: Infinity, repeatType: 'reverse', ease: [0.4, 0, 0.2, 1] },
        }
      case 'drift':
        return {
          animate: { x: ['0%', '-3%', '-1%', '0%'], y: ['0%', '-2%', '-0.5%', '0%'] },
          transition: { duration: 24, repeat: Infinity, ease: 'easeInOut' },
        }
      default:
        return null
    }
  }, [mode])
}

function pickNextIndex(prev, len, shuffle) {
  if (len <= 1) return 0
  if (!shuffle) return (prev + 1) % len
  let next = prev
  let guard = 0
  while (next === prev && guard < 20) {
    next = Math.floor(Math.random() * len)
    guard += 1
  }
  return next
}

const Hero = () => {
  /** Null until RPC resolves — never hydrate with fallback copy that would fade in before the real config. */
  const [cfg, setCfg] = useState(null)
  const phrases = cfg?.phrases ?? []
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    let alive = true
    fetchPublicHero().then((c) => {
      if (alive) setCfg(c)
    })
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    if (!cfg) return
    setIdx((i) => Math.max(0, Math.min(i, Math.max(0, phrases.length - 1))))
  }, [cfg, phrases.length])

  useEffect(() => {
    if (!cfg || phrases.length <= 1) return undefined
    const ms = cfg.rotate_seconds * 1000
    const id = window.setInterval(() => {
      setIdx((prev) => pickNextIndex(prev, phrases.length, cfg.shuffle))
    }, ms)
    return () => window.clearInterval(id)
  }, [cfg, phrases.length, cfg?.rotate_seconds, cfg?.shuffle])

  const phrase = phrases[idx]
  const hasBg = Boolean(cfg?.background_image_url)
  const overlayClass = OVERLAY_CLASSES[cfg?.overlay] ?? OVERLAY_CLASSES.none
  const textFx = TEXT_EFFECT_CLASSES[cfg?.text_effect] ?? ''
  const textBlocks = textMotionBlocks(cfg?.text_animation)
  const bgMotion = useBackgroundMotion(cfg?.background_animation ?? 'none')

  const hasHeadlines = phrases.length > 0

  if (cfg === null) {
    return null
  }

  if (!hasHeadlines && !hasBg) {
    return null
  }

  return (
    <section
      className={`relative flex h-screen items-center justify-center overflow-hidden ${
        hasBg ? 'bg-muted' : 'bg-gradient-to-b from-background via-background to-secondary'
      }`}
      style={{ marginTop: 0, position: 'relative', zIndex: 1 }}
    >
      {hasBg ? (
        <>
          <div className="absolute inset-0 z-0 overflow-hidden">
            {!bgMotion ? (
              <img
                src={cfg.background_image_url}
                alt=""
                loading="eager"
                decoding="async"
                className="h-full w-full object-cover"
              />
            ) : (
              <motion.div
                className="h-full w-full will-change-transform"
                style={{ transformOrigin: '50% 45%' }}
                initial={false}
                animate={bgMotion.animate}
                transition={bgMotion.transition}
              >
                <img
                  src={cfg.background_image_url}
                  alt=""
                  loading="eager"
                  decoding="async"
                  className="h-full min-h-[100vh] min-w-full object-cover"
                />
              </motion.div>
            )}
          </div>
          {overlayClass ? (
            <div className={`pointer-events-none absolute inset-0 z-[1] ${overlayClass}`} aria-hidden />
          ) : null}
        </>
      ) : null}

      {hasHeadlines && phrase ? (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-[2] mx-auto w-full max-w-[1400px] px-[5%] text-left sm:text-center"
        >
          <AnimatePresence mode="wait">
            <motion.h1
              key={`hero-line-${idx}`}
              {...textBlocks}
              className="mb-4 text-2xl font-medium leading-[1.1] tracking-tight sm:text-3xl md:text-[2.25rem]"
            >
              <span className={textFx}>
                <span>{phrase.line1}</span>
                {phrase.line2 ? (
                  <>
                    <br className="block sm:hidden" />
                    <span className="hidden sm:inline"> </span>
                    <span>{phrase.line2}</span>
                  </>
                ) : null}
              </span>
            </motion.h1>
          </AnimatePresence>
        </motion.div>
      ) : null}
    </section>
  )
}

export default Hero
